import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraIconButton, SpecraSegmentedControl } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Brain, Pencil, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { memoryChipLabel, useDeleteMemory, useMemories, useUpsertMemory } from '../../hooks/useMemories';
import { memoryScopeFromEnvelope, memoryScopesFromEnvelope } from '../../lib/chat/memoryScope';
import { SopsTab } from './SopsTab';
import { TeamTab } from './TeamTab';
export function MemoryPanel(props) {
    if (!props.isOpen) return null;
    return /*#__PURE__*/ _jsx(MemoryPanelContent, {
        ...props
    });
}
function MemoryPanelContent({ envelope, onClose, draft, onDraftConsumed, initialTab }) {
    const scopes = useMemo(()=>memoryScopesFromEnvelope(envelope), [
        envelope
    ]);
    const primaryScope = useMemo(()=>memoryScopeFromEnvelope(envelope), [
        envelope
    ]);
    const memoriesQuery = useMemories(scopes);
    const upsert = useUpsertMemory(scopes);
    const del = useDeleteMemory(scopes);
    const [editingId, setEditingId] = useState(null);
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [kind, setKind] = useState('personal');
    const [editScopeKind, setEditScopeKind] = useState();
    const [editScopeId, setEditScopeId] = useState();
    const [tab, setTab] = useState(initialTab ?? 'notes');
    useEffect(()=>{
        if (!draft) return;
        setTab('notes');
        setEditingId(null);
        setTitle(draft.title);
        setBody(draft.body);
        setKind(primaryScope.scopeKind ? 'project' : 'personal');
        onDraftConsumed?.();
    }, [
        draft,
        onDraftConsumed,
        primaryScope.scopeKind
    ]);
    const resetForm = useCallback(()=>{
        setEditingId(null);
        setTitle('');
        setBody('');
        setKind(primaryScope.scopeKind ? 'project' : 'personal');
        setEditScopeKind(undefined);
        setEditScopeId(undefined);
    }, [
        primaryScope.scopeKind
    ]);
    const startEdit = useCallback((row)=>{
        setEditingId(row.id);
        setTitle(row.title);
        setBody(row.body);
        setKind(row.kind === 'project' ? 'project' : 'personal');
        setEditScopeKind(row.scopeKind || undefined);
        setEditScopeId(row.scopeId || undefined);
    }, []);
    const handleSave = useCallback(async ()=>{
        const trimmedTitle = title.trim();
        const trimmedBody = body.trim();
        if (!trimmedTitle && !trimmedBody) return;
        const projectScopeKind = editingId ? editScopeKind : primaryScope.scopeKind;
        const projectScopeId = editingId ? editScopeId : primaryScope.scopeId;
        const isProject = kind === 'project' && projectScopeKind && projectScopeId;
        await upsert.mutateAsync({
            id: editingId ?? undefined,
            kind: isProject ? 'project' : 'personal',
            title: trimmedTitle,
            body: trimmedBody,
            scopeKind: isProject ? projectScopeKind : undefined,
            scopeId: isProject ? projectScopeId : undefined
        });
        resetForm();
    }, [
        body,
        editScopeId,
        editScopeKind,
        editingId,
        kind,
        primaryScope,
        resetForm,
        title,
        upsert
    ]);
    const memories = memoriesQuery.data ?? [];
    return /*#__PURE__*/ _jsx("div", {
        className: "fixed inset-0 z-50 flex items-center justify-center bg-black/50",
        "data-testid": Testids.memory.panel,
        children: /*#__PURE__*/ _jsxs("div", {
            className: "mx-4 flex max-h-[85vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl dark:bg-gray-800",
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center gap-2",
                            children: [
                                /*#__PURE__*/ _jsx(Brain, {
                                    className: "h-5 w-5 text-gray-700 dark:text-gray-200"
                                }),
                                /*#__PURE__*/ _jsx("h2", {
                                    className: "text-lg font-semibold text-gray-900 dark:text-white",
                                    children: "Memory"
                                }),
                                /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                                    className: "ml-3",
                                    label: "Memory section",
                                    size: "sm",
                                    testId: Testids.memory.tabs,
                                    value: tab,
                                    onValueChange: setTab,
                                    options: [
                                        {
                                            value: 'notes',
                                            label: 'Notes'
                                        },
                                        {
                                            value: 'team',
                                            label: 'Team'
                                        },
                                        {
                                            value: 'sops',
                                            label: 'SOPs'
                                        }
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx("button", {
                            type: "button",
                            onClick: onClose,
                            "aria-label": "Close memory panel",
                            children: /*#__PURE__*/ _jsx(X, {
                                className: "h-5 w-5 text-gray-500"
                            })
                        })
                    ]
                }),
                tab === 'sops' ? /*#__PURE__*/ _jsx(SopsTab, {}) : null,
                tab === 'team' ? /*#__PURE__*/ _jsx(TeamTab, {}) : null,
                tab === 'notes' && memories.length > 0 && /*#__PURE__*/ _jsx("div", {
                    className: "border-b border-gray-100 px-5 py-2 text-sm text-muted-foreground dark:border-gray-700",
                    "data-testid": Testids.memory.chip,
                    children: memoryChipLabel(memories)
                }),
                tab === 'notes' && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex-1 overflow-y-auto p-5",
                            "data-testid": Testids.memory.list,
                            children: [
                                memoriesQuery.isLoading && /*#__PURE__*/ _jsx("p", {
                                    className: "text-sm text-muted-foreground",
                                    children: "Loading…"
                                }),
                                memories.map((row)=>/*#__PURE__*/ _jsx("div", {
                                        className: "mb-3 rounded-lg border border-gray-200 p-3 dark:border-gray-700",
                                        "data-testid": Testids.memory.item,
                                        children: /*#__PURE__*/ _jsxs("div", {
                                            className: "flex items-start justify-between gap-2",
                                            children: [
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "font-medium text-gray-900 dark:text-white",
                                                            children: row.title || '(untitled)'
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "mt-1 text-sm text-gray-600 dark:text-gray-300",
                                                            children: row.body
                                                        }),
                                                        /*#__PURE__*/ _jsxs("p", {
                                                            className: "mt-2 text-xs text-muted-foreground",
                                                            children: [
                                                                row.kind,
                                                                row.updatedBy ? ` · ${row.updatedBy}` : '',
                                                                row.updatedAt ? ` · ${row.updatedAt.toLocaleString()}` : ''
                                                            ]
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "flex gap-1",
                                                    children: [
                                                        /*#__PURE__*/ _jsx(SpecraIconButton, {
                                                            label: "Edit memory",
                                                            size: "sm",
                                                            variant: "secondary",
                                                            onClick: ()=>startEdit(row),
                                                            children: /*#__PURE__*/ _jsx(Pencil, {
                                                                className: "h-3.5 w-3.5"
                                                            })
                                                        }),
                                                        /*#__PURE__*/ _jsx(SpecraIconButton, {
                                                            label: "Delete memory",
                                                            size: "sm",
                                                            variant: "secondary",
                                                            "data-testid": Testids.memory.deleteButton,
                                                            onClick: ()=>del.mutate(row),
                                                            children: /*#__PURE__*/ _jsx(Trash2, {
                                                                className: "h-3.5 w-3.5"
                                                            })
                                                        })
                                                    ]
                                                })
                                            ]
                                        })
                                    }, row.id)),
                                !memoriesQuery.isLoading && memories.length === 0 && /*#__PURE__*/ _jsx("p", {
                                    className: "text-sm text-muted-foreground",
                                    children: "No notes yet for this context."
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "border-t border-gray-200 p-5 dark:border-gray-700",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "mb-3 flex gap-3",
                                    children: [
                                        /*#__PURE__*/ _jsxs("label", {
                                            className: "flex items-center gap-1 text-sm",
                                            children: [
                                                /*#__PURE__*/ _jsx("input", {
                                                    type: "radio",
                                                    checked: kind === 'personal',
                                                    onChange: ()=>setKind('personal')
                                                }),
                                                "Personal"
                                            ]
                                        }),
                                        primaryScope.scopeKind && /*#__PURE__*/ _jsxs("label", {
                                            className: "flex items-center gap-1 text-sm",
                                            children: [
                                                /*#__PURE__*/ _jsx("input", {
                                                    type: "radio",
                                                    checked: kind === 'project',
                                                    onChange: ()=>setKind('project')
                                                }),
                                                "Project"
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("input", {
                                    className: "mb-2 w-full rounded border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700",
                                    placeholder: "Title",
                                    value: title,
                                    onChange: (e)=>setTitle(e.target.value),
                                    "data-testid": Testids.memory.titleInput
                                }),
                                /*#__PURE__*/ _jsx("textarea", {
                                    className: "mb-3 w-full rounded border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700",
                                    rows: 3,
                                    placeholder: "Note body",
                                    value: body,
                                    onChange: (e)=>setBody(e.target.value),
                                    "data-testid": Testids.memory.bodyInput
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex justify-end gap-2",
                                    children: [
                                        editingId && /*#__PURE__*/ _jsx(SpecraButton, {
                                            variant: "secondary",
                                            size: "sm",
                                            onClick: resetForm,
                                            children: "Cancel edit"
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraButton, {
                                            size: "sm",
                                            onClick: handleSave,
                                            disabled: upsert.isPending || !title.trim() && !body.trim(),
                                            "data-testid": Testids.memory.saveButton,
                                            children: editingId ? 'Update' : 'Add note'
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                })
            ]
        })
    });
}
